import { useState } from "react";
import axios from "axios";
import { Instagram, MapPin, Mail, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { TESTIDS, INSTAGRAM_URL, INSTAGRAM_HANDLE, LOCATION } from "@/lib/site";
import { useReveal } from "@/lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const r = useReveal();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (
      !form.full_name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.reason.trim()
    ) {
      toast.error("Por favor completa todos los campos.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ full_name: "", email: "", phone: "", reason: "" });
      toast.success("Hemos recibido tu mensaje. Berenice te escribirá pronto.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "No pudimos enviar tu mensaje. Inténtalo de nuevo en unos minutos.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid={TESTIDS.contact.section}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Decorative orb */}
      <div aria-hidden className="absolute -top-32 right-1/4 w-[500px] h-[500px] rounded-full bg-dusty-rose/25 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className={`${r.className} lg:col-span-5`}>
          <p className="text-[0.78rem] tracking-[0.22em] uppercase text-accent-magenta mb-4">
            Contacto
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.4rem] text-editorial-dark leading-[1.05]">
            Escríbeme con
            <span className="italic text-terracotta"> calma</span>.
          </h2>
          <p className="mt-5 text-editorial-gray leading-relaxed max-w-md">
            Cada proceso emocional merece un primer paso. Cuéntame brevemente lo que te
            está sucediendo y te responderé personalmente para acompañarte en lo que
            necesites.
          </p>

          <div className="mt-9 space-y-4">
            <InfoRow icon={<MapPin size={18} />} label="Ubicación" value={LOCATION} />
            <InfoRow
              icon={<Mail size={18} />}
              label="Correo"
              value="psic.berenicepavon@gmail.com"
            />
            <a
              data-testid={TESTIDS.contact.instagramBtn}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-2xl bg-warm-beige/60 border border-dusty-rose/30 px-5 py-4 hover:border-accent-magenta/40 transition-colors group"
            >
              <span className="inline-flex w-10 h-10 rounded-full bg-warm-white items-center justify-center text-accent-magenta">
                <Instagram size={18} />
              </span>
              <div className="flex-1">
                <p className="text-[0.72rem] tracking-[0.22em] uppercase text-editorial-gray">
                  Instagram
                </p>
                <p className="font-medium text-editorial-dark">{INSTAGRAM_HANDLE}</p>
              </div>
              <svg
                className="text-editorial-gray group-hover:text-accent-magenta group-hover:translate-x-1 transition-all"
                width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M7 17 17 7M9 7h8v8" />
              </svg>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative rounded-[2rem] bg-warm-white border border-dusty-rose/30 p-7 md:p-10 shadow-lg shadow-editorial-dark/5">
            {success ? (
              <div
                data-testid={TESTIDS.contact.success}
                className="text-center py-10"
              >
                <span className="inline-flex w-16 h-16 rounded-full bg-sage/30 text-[#3F5340] items-center justify-center mb-5">
                  <CheckCircle2 size={32} />
                </span>
                <h3 className="font-serif text-3xl text-editorial-dark">
                  Gracias por escribirme.
                </h3>
                <p className="mt-3 text-editorial-gray max-w-md mx-auto">
                  He recibido tu mensaje. Te responderé personalmente lo antes posible,
                  con el tiempo y el cuidado que tu proceso merece.
                </p>
                <Button
                  onClick={() => setSuccess(false)}
                  variant="outline"
                  className="mt-7 rounded-full border-editorial-gray/30 hover:border-accent-magenta/50 hover:text-accent-magenta"
                >
                  Enviar otro mensaje
                </Button>
              </div>
            ) : (
              <form
                data-testid={TESTIDS.contact.form}
                onSubmit={submit}
                className="space-y-5"
                noValidate
              >
                <Field label="Nombre completo" htmlFor="full_name">
                  <Input
                    id="full_name"
                    data-testid={TESTIDS.contact.fieldName}
                    type="text"
                    placeholder="¿Cómo te llamas?"
                    value={form.full_name}
                    onChange={onChange("full_name")}
                    required
                    className="bg-warm-white border-dusty-rose/40 rounded-xl h-12 focus-visible:ring-accent-magenta/40 focus-visible:border-accent-magenta/50 placeholder:text-editorial-gray/60"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Correo electrónico" htmlFor="email">
                    <Input
                      id="email"
                      data-testid={TESTIDS.contact.fieldEmail}
                      type="email"
                      placeholder="tu@correo.com"
                      value={form.email}
                      onChange={onChange("email")}
                      required
                      className="bg-warm-white border-dusty-rose/40 rounded-xl h-12 focus-visible:ring-accent-magenta/40 focus-visible:border-accent-magenta/50 placeholder:text-editorial-gray/60"
                    />
                  </Field>
                  <Field label="Teléfono o WhatsApp" htmlFor="phone">
                    <Input
                      id="phone"
                      data-testid={TESTIDS.contact.fieldPhone}
                      type="tel"
                      placeholder="Ej. +52 220 ..."
                      value={form.phone}
                      onChange={onChange("phone")}
                      required
                      className="bg-warm-white border-dusty-rose/40 rounded-xl h-12 focus-visible:ring-accent-magenta/40 focus-visible:border-accent-magenta/50 placeholder:text-editorial-gray/60"
                    />
                  </Field>
                </div>
                <Field label="Motivo de consulta" htmlFor="reason">
                  <Textarea
                    id="reason"
                    data-testid={TESTIDS.contact.fieldReason}
                    rows={5}
                    placeholder="Cuéntame brevemente qué te está sucediendo o qué necesitas. No hace falta tener todas las palabras."
                    value={form.reason}
                    onChange={onChange("reason")}
                    required
                    className="bg-warm-white border-dusty-rose/40 rounded-xl focus-visible:ring-accent-magenta/40 focus-visible:border-accent-magenta/50 placeholder:text-editorial-gray/60 resize-none"
                  />
                </Field>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                  <p className="text-xs text-editorial-gray max-w-xs">
                    Tu información se trata con total confidencialidad. Solo se utilizará
                    para responder a tu consulta.
                  </p>
                  <Button
                    data-testid={TESTIDS.contact.submit}
                    type="submit"
                    disabled={submitting}
                    className="rounded-full bg-accent-magenta hover:bg-accent-magenta/90 text-warm-white px-7 h-12 text-sm shadow-md shadow-accent-magenta/20 hover:-translate-y-0.5 transition-all"
                  >
                    {submitting ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="block w-3 h-3 rounded-full border-2 border-warm-white border-t-transparent animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        Enviar mensaje
                        <Send size={16} />
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label
        htmlFor={htmlFor}
        className="block text-[0.78rem] tracking-[0.18em] uppercase text-editorial-gray mb-2"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex w-10 h-10 rounded-full bg-warm-beige/70 border border-dusty-rose/30 items-center justify-center text-accent-magenta">
        {icon}
      </span>
      <div>
        <p className="text-[0.72rem] tracking-[0.22em] uppercase text-editorial-gray">
          {label}
        </p>
        <p className="font-medium text-editorial-dark">{value}</p>
      </div>
    </div>
  );
}
